"use strict";
import { ApolloLink } from "@apollo/client";
import { asyncMap } from "@apollo/client/utilities";
import { Buffer } from "buffer";
import { getNativeAddress } from "constants/uniswapAddresses";
import {
  Currency,
  TokenBalancePartsFragmentDoc,
  TokenDocument
} from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { GQLQueries } from "data/graphql/uniswap-data-api/queries";
import { fromGraphQLChain } from "features/chains/utils";
import { currencyIdToContractInput } from "features/dataApi/utils";
import { fetchOnChainBalances } from "features/portfolio/portfolioUpdates/fetchOnChainBalances";
import { makeSelectTokenBalanceOverridesForWalletAddress } from "features/portfolio/slice/selectors";
import {
  removeExpiredBalanceOverrides,
  removeTokenFromBalanceOverride
} from "features/portfolio/slice/slice";
import { buildCurrencyId, currencyIdToAddress, currencyIdToChain, isNativeCurrencyAddress } from "utils/uniswapCurrencyId";
import { logger } from "utilities/src/logger/logger";
import { fetchTokenData } from "features/routing/api/bscSubgraph";
import { isBscChain } from "constants/bscContracts";
const APPROXIMATE_EQUALITY_THRESHOLD_PERCENT = 0.02;
export function getInstantTokenBalanceUpdateApolloLink({ reduxStore }) {
  return new ApolloLink((operation, forward) => {
    if (operation.operationName !== GQLQueries.PortfolioBalances) {
      return forward(operation);
    }
    return asyncMap(forward(operation), async (response) => {
      try {
        const walletAddress = operation.variables.ownerAddress;
        reduxStore.dispatch(removeExpiredBalanceOverrides());
        const selectTokenBalanceOverridesForWalletAddress = makeSelectTokenBalanceOverridesForWalletAddress();
        const tokenBalanceOverrides = selectTokenBalanceOverridesForWalletAddress(reduxStore.getState(), walletAddress);
        if (!tokenBalanceOverrides) {
          return response;
        }
        logger.debug(
          "getInstantTokenBalanceUpdateApolloLink.ts",
          "getInstantTokenBalanceUpdateApolloLink",
          "[ITBU] Maybe overriding token balance in apollo response",
          tokenBalanceOverrides
        );
        if (!response?.data?.portfolios) {
          logger.warn(
            "getInstantTokenBalanceUpdateApolloLink.ts",
            "getInstantTokenBalanceUpdateApolloLink",
            "[ITBU] Unexpected response from `PortfolioBalances` query",
            { response }
          );
          return response;
        }
        const data = response.data;
        const tokenBalances = data?.portfolios?.[0]?.tokenBalances;
        if (!tokenBalances) {
          logger.warn(
            "getInstantTokenBalanceUpdateApolloLink.ts",
            "getInstantTokenBalanceUpdateApolloLink",
            "[ITBU] No `tokenBalances` array found in `PortfolioBalances` response",
            { response }
          );
          return response;
        }
        const { cache: apolloCache } = operation.getContext();
        const onchainBalancesByCurrencyId = await fetchOnChainBalances({
          apolloCache,
          cachedPortfolio: data.portfolios[0],
          accountAddress: walletAddress,
          currencyIds: new Set(Object.keys(tokenBalanceOverrides))
        });
        const tokenBalanceAlreadyExists = {};
        tokenBalances.forEach((tokenBalance) => {
          if (!tokenBalance) {
            return;
          }
          const chainId = fromGraphQLChain(tokenBalance.token?.chain);
          if (!chainId) {
            logger.warn(
              "getInstantTokenBalanceUpdateApolloLink.ts",
              "getInstantTokenBalanceUpdateApolloLink",
              "[ITBU] No `chain` found for token",
              { tokenBalance }
            );
            return;
          }
          const tokenAddress = tokenBalance.token?.address ?? getNativeAddress(chainId);
          const currencyId = buildCurrencyId(chainId, tokenAddress).toLowerCase();
          const tokenBalanceOverride = tokenBalanceOverrides[currencyId];
          if (!tokenBalanceOverride) {
            return;
          }
          tokenBalanceAlreadyExists[currencyId] = true;
          const onchainBalance = onchainBalancesByCurrencyId.get(currencyId);
          if (!onchainBalance?.quantity) {
            logger.warn(
              "getInstantTokenBalanceUpdateApolloLink.ts",
              "getInstantTokenBalanceUpdateApolloLink",
              "[ITBU] No `onchainBalance.quantity` found for token",
              { currencyId, walletAddress }
            );
            return;
          }
          const onchainQuantity = onchainBalance.quantity;
          const cachedQuantity = tokenBalance.quantity;
          const areQuantitiesAproximatelyEqual = typeof cachedQuantity === "number" ? Math.abs(onchainQuantity - cachedQuantity) / cachedQuantity <= APPROXIMATE_EQUALITY_THRESHOLD_PERCENT : false;
          if (areQuantitiesAproximatelyEqual) {
            logger.debug(
              "getInstantTokenBalanceUpdateApolloLink.ts",
              "getInstantTokenBalanceUpdateApolloLink",
              "[ITBU] Quantities are aproximately equal, removing from store",
              { cachedQuantity, onchainQuantity }
            );
            reduxStore.dispatch(
              removeTokenFromBalanceOverride({
                ownerAddress: walletAddress,
                chainId,
                tokenAddress
              })
            );
            return;
          }
          tokenBalance.quantity = onchainQuantity;
          logger.debug(
            "getInstantTokenBalanceUpdateApolloLink.ts",
            "getInstantTokenBalanceUpdateApolloLink",
            "[ITBU] Overriding quantity",
            { cachedQuantity, onchainQuantity }
          );
          if (cachedQuantity && tokenBalance.denominatedValue?.value) {
            tokenBalance.denominatedValue = {
              ...tokenBalance.denominatedValue,
              value: tokenBalance.denominatedValue.value * onchainQuantity / cachedQuantity
            };
          }
        });
        if (tokenBalanceOverrides.length === tokenBalanceAlreadyExists.length) {
          return response;
        }
        const missingTokenBalances = Object.keys(tokenBalanceOverrides).filter(
          (currencyId) => !tokenBalanceAlreadyExists[currencyId]
        );
        for (const currencyId of missingTokenBalances) {
          const onchainBalanceQuantity = onchainBalancesByCurrencyId.get(currencyId)?.quantity;
          const denominatedValue = onchainBalancesByCurrencyId.get(currencyId)?.denominatedValue ?? null;
          if (onchainBalanceQuantity === void 0) {
            logger.warn(
              "getInstantTokenBalanceUpdateApolloLink.ts",
              "getInstantTokenBalanceUpdateApolloLink",
              "[ITBU] No `onchainBalance.quantity` found for token",
              { currencyId, walletAddress }
            );
            continue;
          }
          const newTokenBalance = await createTokenBalance({
            apolloCache,
            ownerAddress: walletAddress,
            currencyId,
            onchainBalanceQuantity,
            denominatedValue
          });
          if (!newTokenBalance) {
            continue;
          }
          tokenBalances.push(newTokenBalance);
        }
        return response;
      } catch (error) {
        logger.error(error, {
          tags: {
            file: "getInstantTokenBalanceUpdateApolloLink.ts",
            function: "getInstantTokenBalanceUpdateApolloLink"
          }
        });
        return response;
      }
    });
  });
}
export async function createTokenBalanceRef({
  apolloCache,
  ownerAddress,
  currencyId,
  onchainBalanceQuantity,
  denominatedValue
}) {
  const currencyAddress = currencyIdToAddress(currencyId);
  const chainId = currencyIdToChain(currencyId);
  if (!currencyAddress || !chainId) {
    logger.warn("getInstantTokenBalanceUpdateApolloLink.ts", "createTokenBalance", "[ITBU] Unable to parse currencyId", {
      currencyId
    });
    return null;
  }
  let token;
  if (isBscChain(chainId)) {
    if (isNativeCurrencyAddress(chainId, currencyAddress)) {
      token = {
        __typename: "Token",
        id: getNativeAddress(chainId),
        chain: "ETHEREUM",
        // GraphQL enum hijacked for BSC (no BNB enum exists upstream)
        address: null,
        // Native currency has null address in GraphQL
        decimals: 18,
        name: "BNB",
        symbol: "BNB",
        standard: "NATIVE",
        feeData: null,
        project: null,
        protectionInfo: null,
        market: null
      };
    } else {
      const tokenData = await fetchTokenData(currencyAddress);
      if (!tokenData) {
        logger.warn("getInstantTokenBalanceUpdateApolloLink.ts", "createTokenBalance", "[ITBU] No token found in BSC subgraph", { currencyId });
        return null;
      }
      token = {
        __typename: "Token",
        id: tokenData.id,
        chain: "ETHEREUM",
        // GraphQL enum for Topaz testnet
        address: tokenData.id,
        decimals: parseInt(tokenData.decimals),
        name: tokenData.name,
        symbol: tokenData.symbol,
        standard: "ERC20",
        feeData: null,
        project: null,
        protectionInfo: null,
        market: null
      };
    }
  } else {
    token = apolloCache.readQuery({
      query: TokenDocument,
      variables: currencyIdToContractInput(currencyId)
    })?.token;
    if (!token) {
      logger.warn("getInstantTokenBalanceUpdateApolloLink.ts", "createTokenBalance", "[ITBU] No `token` found", {
        currencyId
      });
      return null;
    }
  }
  const tokenBalanceId = generateEntityId("TokenBalance", [ownerAddress, token.id, Currency.Usd]);
  logger.debug(
    "getInstantTokenBalanceUpdateApolloLink.ts",
    "createTokenBalanceRef",
    `[ITBU]Calling apolloCache.writeFragment for ${currencyId}`,
    {
      onchainBalanceQuantity,
      denominatedValue
    }
  );
  const newTokenBalanceRef = apolloCache.writeFragment({
    data: {
      __typename: "TokenBalance",
      id: tokenBalanceId,
      quantity: onchainBalanceQuantity,
      denominatedValue: denominatedValue ? {
        __typename: "Amount",
        value: denominatedValue.value,
        currency: denominatedValue.currency
      } : null,
      isHidden: false,
      token,
      tokenProjectMarket: {
        relativeChange24: null
      }
    },
    fragment: TokenBalancePartsFragmentDoc,
    fragmentName: "TokenBalanceParts"
  });
  if (!newTokenBalanceRef) {
    logger.warn(
      "getInstantTokenBalanceUpdateApolloLink.ts",
      "createTokenBalanceRef",
      "[ITBU] Failed to write `newTokenBalanceRef`",
      {
        tokenBalanceId,
        ownerAddress,
        currencyId
      }
    );
    return null;
  }
  return newTokenBalanceRef;
}
async function createTokenBalance({
  apolloCache,
  ownerAddress,
  currencyId,
  onchainBalanceQuantity,
  denominatedValue
}) {
  const newTokenBalanceRef = await createTokenBalanceRef({
    apolloCache,
    ownerAddress,
    currencyId,
    onchainBalanceQuantity,
    denominatedValue
  });
  if (!newTokenBalanceRef) {
    return null;
  }
  const newTokenBalance = apolloCache.readFragment({
    id: apolloCache.identify(newTokenBalanceRef),
    fragment: TokenBalancePartsFragmentDoc,
    fragmentName: "TokenBalanceParts"
  });
  if (!newTokenBalance) {
    logger.warn(
      "getInstantTokenBalanceUpdateApolloLink.ts",
      "createTokenBalance",
      "[ITBU] Failed to read `newTokenBalance` from cache",
      { currencyId, ownerAddress }
    );
    return null;
  }
  return newTokenBalance;
}
function generateEntityId(typeName, identifiers) {
  const id = identifiers.map((x) => {
    return x === null ? "null" : x;
  }).join("_");
  return Buffer.from(`${typeName}:${id}`).toString("base64");
}
